import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import {
  useInspectionDownloadPdfMutation,
  useLazyInspectionPdfPreviewQuery,
  useSetInspectionImmutableMutation,
} from "@/services/backend/inspections/service";
import { InspectionReportNumber } from "@/shared/components/domain/inspection/inspection-report-number";
import { ArtefactImmutabilityReason } from "@/services/backend/inspections/inspection";
import { ErrDocumentNotReady } from "@bitory-gmbh/bsm";
import { ErrValidationFailed } from "@bitory-gmbh/nidavellir";

export function InspectionPdf({ inspectionId }: { inspectionId: string }) {
  const { user } = useAuth();
  const canAccess = useFeatureAuth("inspections.inspection.show");
  const canDownload = useFeatureAuth("inspections.inspection.download");
  const [open, setOpen] = useState(false);
  const [triggerPreview, { data, isFetching, error }] =
    useLazyInspectionPdfPreviewQuery();
  const [notReadyError, setNotReadyError] =
    useState<ErrDocumentNotReady | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    // Regenerate the PDF from current backend state every time the sheet opens
    // so edits to the report's underlying data are reflected.
    if (next) {
      triggerPreview({ inspectionId });
    }
  };

  return (
    <DocumentSheet open={open} onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline" disabled={!canAccess}>
          PDF
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Prüfbericht")}
              documentNumber={
                <InspectionReportNumber artefactId={inspectionId} />
              }
            />
            <DocumentSheetActionsContent>
              <DocumentNotReadyAlert
                error={notReadyError as unknown as ErrValidationFailed}
                document={t("Prüfbericht")}
              />
              <DocumentSheetImmutabilityWarning
                title={t("Hinweis")}
                description={t(
                  "Um einen Prüfbericht festzuschreiben, müssen alle erforderlichen Felder ausgefüllt sein. Sobald der Bericht festgeschrieben ist, kann er nicht mehr geändert werden.",
                )}
              />
              <Separator />
              <DocumentSheetActionsSetImmutableMutation
                request={{
                  id: inspectionId,
                  reason: ArtefactImmutabilityReason.InspectionReleased,
                  setBy: user?.id || "",
                }}
                mutation={useSetInspectionImmutableMutation}
                description={t(
                  "Der Prüfbericht wird festgeschrieben. Danach kann er nicht mehr geändert werden.",
                )}
                onError={(err) => {
                  if (rtkErrIsErrDocumentNotReady(err)) {
                    setNotReadyError(err.data);
                  }
                }}
              />
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  disabled={!canDownload}
                  inspectionId={inspectionId}
                  text={t("Bericht herunterladen")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  inspectionId,
  text,
  disabled = false,
}: {
  inspectionId: string;
  text: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useInspectionDownloadPdfMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  const { user } = useAuth();

  const doDownload = () => {
    if (isLoading || disabled) {
      return;
    }

    download({
      inspectionId,
      download: true,
      downloadBy: user?.id as string,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bericht herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Bericht heruntergeladen wird, wird er festgeschrieben. Es kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
